import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Send, Save, Ban, ArrowLeft, AlertCircle, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { DocstatusPill, StatusPill } from '@/components/StatusPill';
import { ApprovalWidget } from '@/components/ApprovalWidget';
import { Timeline } from '@/components/Timeline';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { money, date } from '@/lib/format';
import { toast } from '@/components/Toaster';

type Kind = 'revaluation' | 'impairment' | 'revaluation_down';

interface Asset   { id: string; name: string; asset_name: string; status: string; gross_purchase_amount: string; accumulated_depreciation: string; }
interface Account { id: string; name: string; account_number?: string; root_type: string; }
interface ValueAdjustment {
  id: string; name: string;
  asset_id: string;
  adjustment_date: string;
  kind: Kind;
  amount: string;
  reason?: string;
  revaluation_surplus_account_id?: string;
  impairment_loss_account_id?: string;
  posted_voucher_id?: string;
  docstatus: 0 | 1 | 2;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

export function AssetValueAdjustmentForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['ava', id],
    enabled: !isNew,
    queryFn: () => api<ValueAdjustment>(`/assets/asset-value-adjustments/${id}`),
  });
  const { data: assetsResp   } = useQuery({ queryKey: ['assets'],   queryFn: () => api<{ items: Asset[]   }>('/assets/assets') });
  const { data: accountsResp } = useQuery({ queryKey: ['accounts'], queryFn: () => api<{ items: Account[] }>('/accounting/accounts') });
  const assets   = assetsResp?.items   ?? [];
  const accounts = accountsResp?.items ?? [];

  const [assetID, setAssetID]         = useState<string>('');
  const [kind, setKind]               = useState<Kind>('revaluation');
  const [adjDate, setAdjDate]         = useState<string>(todayISO());
  const [amount, setAmount]           = useState<string>('');
  const [reason, setReason]           = useState<string>('');
  const [surplusID, setSurplusID]     = useState<string>('');
  const [impairmentID, setImpairmentID] = useState<string>('');
  const [err, setErr]                 = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setAssetID(existing.asset_id);
    setKind(existing.kind);
    setAdjDate(existing.adjustment_date.slice(0, 10));
    setAmount(existing.amount);
    setReason(existing.reason ?? '');
    setSurplusID(existing.revaluation_surplus_account_id ?? '');
    setImpairmentID(existing.impairment_loss_account_id ?? '');
  }, [existing]);

  const asset = assets.find((a) => a.id === assetID);

  const assetOptions = useMemo(
    () => assets.map((a) => ({ value: a.id, label: a.asset_name, description: a.name })),
    [assets],
  );
  const accountOptions = useMemo(
    () => accounts.map((a) => ({
      value: a.id, label: a.name,
      description: [a.account_number, a.root_type].filter(Boolean).join(' · '),
    })),
    [accounts],
  );

  const editable  = isNew || existing?.docstatus === 0;
  const submitted = existing?.docstatus === 1;
  const cancelled = existing?.docstatus === 2;
  const needsSurplus    = kind === 'revaluation' || kind === 'revaluation_down';
  const needsImpairment = kind === 'impairment';

  function validate(): string[] {
    const out: string[] = [];
    if (!assetID) out.push('Pick an asset.');
    if (!adjDate) out.push('Adjustment date is required.');
    if (!amount || Number(amount) <= 0) out.push('Amount must be a positive number.');
    if (needsSurplus && !surplusID) out.push('Revaluation surplus account is required.');
    if (needsImpairment && !impairmentID) out.push('Impairment loss account is required.');
    return out;
  }

  function buildBody() {
    return {
      asset_id:        assetID,
      adjustment_date: adjDate,
      kind,
      amount,
      reason: reason || undefined,
      revaluation_surplus_account_id: needsSurplus ? surplusID || undefined : undefined,
      impairment_loss_account_id:     needsImpairment ? impairmentID || undefined : undefined,
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<ValueAdjustment>('/assets/asset-value-adjustments', { method: 'POST', body: buildBody() }),
    onSuccess: (v) => {
      toast.success('Draft saved', v.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/assets/asset-value-adjustments'] });
      navigate({ to: `/assets/asset-value-adjustments/${v.id}` as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const submitMutation = useMutation({
    mutationFn: () => api<ValueAdjustment>(`/assets/asset-value-adjustments/${id}/submit`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Submitted', 'GL voucher posted; asset balance updated.');
      qc.invalidateQueries({ queryKey: ['ava', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/assets/asset-value-adjustments'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Submit failed.'),
  });
  const cancelMutation = useMutation({
    mutationFn: () => api<ValueAdjustment>(`/assets/asset-value-adjustments/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.warning('Cancelled', 'Reversed GL voucher; asset balance restored.');
      qc.invalidateQueries({ queryKey: ['ava', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/assets/asset-value-adjustments'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Cancel failed.'),
  });

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }

  const kindLabel = kind === 'revaluation' ? 'Revaluation ↑'
    : kind === 'revaluation_down' ? 'Revaluation ↓' : 'Impairment';
  const KindIcon = kind === 'revaluation' ? TrendingUp : kind === 'revaluation_down' ? TrendingDown : AlertTriangle;

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Asset & Inventory', to: '/assets' },
          { label: 'Asset Value Adjustments', to: '/assets/asset-value-adjustments' },
          { label: isNew ? 'New' : (existing?.name ?? '…') },
        ]}
        title={isNew ? 'New Asset Value Adjustment' : (existing?.name ?? '…')}
        status={existing && <DocstatusPill docstatus={existing.docstatus} />}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/assets/asset-value-adjustments' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {editable && isNew && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft <Kbd>⌘S</Kbd>
              </Button>
            )}
            {editable && !isNew && (
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>
                <Send className="size-4" /> Submit
              </Button>
            )}
            {submitted && (
              <Button variant="danger" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]"
      >
        <div className="space-y-4 min-w-0">
          {cancelled && (
            <Card className="!p-3 flex items-center gap-3 bg-danger/5 border-danger/30">
              <AlertCircle className="size-4 text-danger" />
              <span className="text-body text-danger">Cancelled — GL voucher reversed and asset balance restored.</span>
            </Card>
          )}
          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          {!isNew && existing && (
            <ApprovalWidget doctype="asset_value_adjustment" documentId={existing.id} />
          )}

          <Card>
            <CardTitle>Adjustment</CardTitle>
            <div className="mt-4 space-y-4">
              <Field label="Kind">
                <div className="inline-flex rounded-md border border-hairline overflow-hidden">
                  {([
                    { v: 'revaluation' as const,      label: 'Revaluation ↑' },
                    { v: 'revaluation_down' as const, label: 'Revaluation ↓' },
                    { v: 'impairment' as const,       label: 'Impairment' },
                  ]).map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      disabled={!editable || !isNew}
                      onClick={() => setKind(opt.v)}
                      className={'px-3 py-1.5 text-body-sm border-r last:border-r-0 border-hairline ' +
                        (kind === opt.v
                          ? 'bg-brand-green text-on-primary'
                          : 'bg-canvas text-charcoal hover:bg-surface-soft')}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Asset" hint="Required.">
                  <Combobox
                    options={assetOptions}
                    value={assetID || null}
                    onChange={(v) => setAssetID(v ?? '')}
                    placeholder="Pick an asset…"
                    disabled={!editable}
                  />
                </Field>
                <Field label="Adjustment date">
                  <Input type="date" value={adjDate} onChange={(e) => setAdjDate(e.target.value)} disabled={!editable} />
                </Field>
                <Field label="Amount" hint="Positive value. Direction follows the kind above.">
                  <NumericInput value={amount} onChange={setAmount} currencyPrefix="Rp" decimalPlaces={0} placeholder="0" />
                </Field>
                <Field label="Reason" hint="Free text — appears on the timeline and GL voucher.">
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} disabled={!editable} />
                </Field>
              </div>
            </div>
          </Card>

          <Card>
            <CardTitle>GL accounts</CardTitle>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              {needsSurplus && (
                <Field label="Revaluation surplus account" hint="Equity account that holds the surplus.">
                  <Combobox
                    options={accountOptions}
                    value={surplusID || null}
                    onChange={(v) => setSurplusID(v ?? '')}
                    placeholder="Pick account…"
                    disabled={!editable}
                  />
                </Field>
              )}
              {needsImpairment && (
                <Field label="Impairment loss account" hint="Expense account that records the loss.">
                  <Combobox
                    options={accountOptions}
                    value={impairmentID || null}
                    onChange={(v) => setImpairmentID(v ?? '')}
                    placeholder="Pick account…"
                    disabled={!editable}
                  />
                </Field>
              )}
            </div>
            <p className="mt-3 text-caption text-stone">
              Submit posts a single GL voucher. Cancel reverses the voucher and restores the asset balance.
            </p>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card>
            <CardTitle><span className="inline-flex items-center gap-2"><KindIcon className="size-4" /> {kindLabel}</span></CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Docstatus" pill={existing ? <DocstatusPill docstatus={existing.docstatus} /> : <StatusPill tone="neutral">Draft</StatusPill>} />
              {asset && (
                <>
                  <Row label="Asset" value={asset.asset_name} />
                  <Row label="Current gross" value={money(asset.gross_purchase_amount)} />
                  <Row label="Accumulated dep." value={money(asset.accumulated_depreciation)} />
                </>
              )}
              {existing && (
                <Row label="Date" value={date(existing.adjustment_date)} />
              )}
              {existing?.posted_voucher_id && (
                <Row label="GL voucher" value={existing.posted_voucher_id} mono />
              )}
            </div>
          </Card>

          <Card>
            <CardTitle>Postings cheat-sheet</CardTitle>
            <ul className="mt-2 space-y-2 text-caption text-stone">
              <li><strong>Revaluation ↑:</strong> Dr Asset · Cr Revaluation Surplus.</li>
              <li><strong>Revaluation ↓:</strong> Dr Revaluation Surplus · Cr Asset.</li>
              <li><strong>Impairment:</strong> Dr Impairment Loss · Cr Accumulated Depreciation.</li>
            </ul>
          </Card>

          {!isNew && existing && (
            <Timeline doctype="asset_value_adjustment" documentId={existing.id} />
          )}
        </aside>
      </motion.div>
    </>
  );
}

function Row({ label, value, mono, pill }: { label: string; value?: string; mono?: boolean; pill?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      {pill ?? <span className={'text-right ' + (mono ? 'font-mono text-caption text-charcoal' : 'text-ink')}>{value}</span>}
    </div>
  );
}
